import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Brain, Users, Key, Settings, BarChart2, Compass, Network, FolderTree, Target, Calendar, Edit2, FileText, TrendingUp, Activity, BookOpen, ClipboardCheck, GraduationCap } from '../icons';
import { SidebarLayout } from './SidebarLayout';
import { RoleSwitcher } from '../RoleSwitcher';
import { useAuth } from '../../contexts/AuthContext';
import { isUserCoach } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../LoadingSpinner';
import { useCoachNotifications } from '../../hooks/useCoachNotifications';
import type { SidebarItem } from './Sidebar';

export function CoachLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { counts, markSeen } = useCoachNotifications();
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  const [hasTeamsAccess, setHasTeamsAccess] = useState(false);
  const [profileOnlyMode, setProfileOnlyMode] = useState(false);
  const [accountPlan, setAccountPlan] = useState<string | null>(null);
  const [progressEnabled, setProgressEnabled] = useState(true);
  const [pathwaysEnabled, setPathwaysEnabled] = useState(true);
  const [presenceEnabled, setPresenceEnabled] = useState(true);
  const [promoteEnabled, setPromoteEnabled] = useState(true);
  const [profitEnabled, setProfitEnabled] = useState(true);
  const [nlpPracticeEnabled, setNlpPracticeEnabled] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const init = async () => {
      const isCoach = await isUserCoach(user.id);
      if (!isCoach) {
        navigate('/');
        return;
      }

      const { data: coachData } = await supabase
        .from('coaches')
        .select('tier, teams_access_enabled, profile_only_mode, account_plan, free_profile_onboarding_completed, progress_enabled, pathways_enabled, presence_enabled, promote_enabled, profit_enabled')
        .eq('user_id', user.id)
        .maybeSingle() as any;

      const isFreeProfileCoach = coachData?.account_plan === 'FREE_PROFILE_COACH';
      const onboardingDone = coachData?.free_profile_onboarding_completed === true;

      if (isFreeProfileCoach && !onboardingDone && window.location.pathname !== '/coach/onboarding') {
        navigate('/coach/onboarding', { replace: true });
        return;
      }

      setHasAIAccess(coachData?.tier === 'advanced' || coachData?.tier === 'partner');
      setHasTeamsAccess(coachData?.teams_access_enabled === true);
      setProfileOnlyMode(coachData?.profile_only_mode === true);
      setAccountPlan(coachData?.account_plan || null);
      setProgressEnabled(coachData?.progress_enabled !== false);
      setPathwaysEnabled(coachData?.pathways_enabled !== false);
      setPresenceEnabled(coachData?.presence_enabled !== false);
      setPromoteEnabled(coachData?.promote_enabled !== false);
      setProfitEnabled(coachData?.profit_enabled !== false);

      // Check if coach is authorized for NLP practice
      const { data: nlpAccess } = await supabase.rpc('is_coach_nlp_practice_authorized', {
        p_coach_user_id: user.id,
      });
      setNlpPracticeEnabled(nlpAccess || false);

      setAuthorized(true);
      setChecking(false);
    };

    init();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user || !authorized) return;
    const path = location.pathname;
    if (path === '/coach/progress') {
      markSeen('progress');
    } else if (path === '/coach/respondents') {
      markSeen('respondents');
    } else if (path === '/coach/reports' || path === '/coach/transformation-reports') {
      markSeen('reports');
    } else if (path === '/coach/programs') {
      markSeen('programs');
    }
  }, [location.pathname, user, authorized, markSeen]);

  if (authLoading || checking || !authorized) {
    return <LoadingSpinner message="Loading..." />;
  }

  const profileOnly = profileOnlyMode;
  const isFreeProfile = profileOnly && accountPlan === 'FREE_PROFILE_COACH';

  let items: SidebarItem[];

  if (isFreeProfile) {
    items = [
      // Profile — always visible
      { group: 'Profile', label: 'Dashboard', path: '/coach', icon: Brain },
      { group: 'Profile', label: 'Respondents', path: '/coach/respondents', icon: Users, badgeCount: counts.respondents },
      { group: 'Profile', label: 'Assessment Codes', path: '/coach/codes', icon: Key },
      { group: 'Profile', label: 'Projects', path: '/coach/projects', icon: FolderTree, gated: true, gateMessage: 'Not included in your plan' },
      { group: 'Profile', label: 'Teams', path: '/coach/teams', icon: Network, gated: true, gateMessage: 'Not included in your plan' },
      { group: 'Profile', label: 'Profile Resources', path: '/coach/profile-resources', icon: BookOpen },

      // Pattern
      { group: 'Pattern', label: 'Breakthrough Report', path: '/coach/manual-analysis', icon: Brain, gated: true, gateMessage: 'Not included in your plan' },

      // Progress
      { group: 'Progress', label: 'Client Progress', path: '/coach/progress', icon: Activity, gated: true, gateMessage: 'Not included in your plan', badgeCount: counts.progress },
      { group: 'Progress', label: 'Transformation Reports', path: '/coach/transformation-reports', icon: FileText, gated: true, gateMessage: 'Not included in your plan', badgeCount: counts.reports },

      // Pathways
      { group: 'Pathways', label: 'My Assigned Pathways', path: '/coach/my-pathways', icon: BookOpen },
      { group: 'Pathways', label: 'Transformation Pathways', path: '/coach/pathways', icon: Compass, gated: true, gateMessage: 'Not included in your plan' },
      { group: 'Pathways', label: 'Programs', path: '/coach/programs', icon: ClipboardCheck, gated: true, gateMessage: 'Not included in your plan' },
      { group: 'Pathways', label: 'Habit Installation OS™', path: '/coach/habit-os', icon: Target, gated: true, gateMessage: 'Not included in your plan' },

      // Presence
      { group: 'Presence', label: 'Coaching Sessions', path: '/coach/sessions', icon: Calendar, gated: true, gateMessage: 'Not included in your plan' },
      { group: 'Presence', label: 'Session Intelligence™', path: '/coach/session-intelligence', icon: Brain, gated: true, gateMessage: 'Not included in your plan' },

      // Proof
      { group: 'Proof', label: 'Team Reports', path: '/coach/reports', icon: BarChart2, gated: true, gateMessage: 'Not included in your plan' },
      { group: 'Proof', label: 'Individual Reports', path: '/coach/respondents', icon: FileText, gated: true, gateMessage: 'Not included in your plan' },

      // Promote
      { group: 'Promote', label: 'Lead Page', path: '/coach/lead-page', icon: Edit2, gated: true, gateMessage: 'Not included in your plan' },
      { group: 'Promote', label: 'Sales Pipeline', path: '/coach/crm', icon: Target, gated: true, gateMessage: 'Not included in your plan' },

      // Profit
      { group: 'Profit', label: 'Profit Center', path: '/coach/profit', icon: TrendingUp, gated: true, gateMessage: 'Not included in your plan' },

      // Settings
      { group: 'Settings', label: 'AI Coach Settings', path: '/coach/ai-settings', icon: Settings, gated: true, gateMessage: 'Not included in your plan' },
      { group: 'Settings', label: 'AI Coach Usage', path: '/coach/ai-usage', icon: BarChart2, gated: true, gateMessage: 'Not included in your plan' },
      { group: 'Settings', label: 'Account Settings', path: '/coach/account', icon: Settings },
      { group: 'Account', label: 'Upgrade', path: '/pricing', icon: TrendingUp },
    ];
  } else {
    items = [
      // Profile — always visible
      { group: 'Profile', label: 'Dashboard', path: '/coach', icon: Brain },
      { group: 'Profile', label: 'Respondents', path: '/coach/respondents', icon: Users, badgeCount: counts.respondents },
      { group: 'Profile', label: 'Assessment Codes', path: '/coach/codes', icon: Key },
      { group: 'Profile', label: 'Projects', path: '/coach/projects', icon: FolderTree, gated: !hasTeamsAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },
      { group: 'Profile', label: 'Teams', path: '/coach/teams', icon: Network, gated: !hasTeamsAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },

      // Pattern
      { group: 'Pattern', label: 'Breakthrough Report', path: '/coach/manual-analysis', icon: Brain, gated: !hasAIAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Requires Advanced tier' },

      // Progress
      { group: 'Progress', label: 'Client Progress', path: '/coach/progress', icon: Activity, gated: profileOnly || !progressEnabled, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable', badgeCount: counts.progress },
      { group: 'Progress', label: 'Transformation Reports', path: '/coach/transformation-reports', icon: FileText, gated: profileOnly || !progressEnabled, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable', badgeCount: counts.reports },

      // Pathways
      { group: 'Pathways', label: 'My Assigned Pathways', path: '/coach/my-pathways', icon: BookOpen },
      { group: 'Pathways', label: 'Transformation Pathways', path: '/coach/pathways', icon: Compass, gated: !hasAIAccess || profileOnly || !pathwaysEnabled, gateMessage: profileOnly ? 'Not included in your plan' : (!hasAIAccess ? 'Requires Advanced tier' : 'Contact your partner to enable') },
      { group: 'Pathways', label: 'Programs', path: '/coach/programs', icon: ClipboardCheck, gated: !hasAIAccess || profileOnly || !pathwaysEnabled, gateMessage: profileOnly ? 'Not included in your plan' : (!hasAIAccess ? 'Requires Advanced tier' : 'Contact your partner to enable'), badgeCount: counts.programs },
      { group: 'Pathways', label: 'Habit Installation OS\u2122', path: '/coach/habit-os', icon: Target, gated: profileOnly || !pathwaysEnabled, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },

      // Presence
      { group: 'Presence', label: 'Coaching Sessions', path: '/coach/sessions', icon: Calendar, gated: profileOnly || !presenceEnabled, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },
      { group: 'Presence', label: 'Session Intelligence\u2122', path: '/coach/session-intelligence', icon: Brain, gated: profileOnly || !presenceEnabled, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },

      // Proof
      { group: 'Proof', label: 'Team Reports', path: '/coach/reports', icon: BarChart2, gated: !hasTeamsAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },
      { group: 'Proof', label: 'Individual Reports', path: '/coach/respondents', icon: FileText, gated: profileOnly, gateMessage: 'Not included in your plan' },

      // Promote
      { group: 'Promote', label: 'Lead Page', path: '/coach/lead-page', icon: Edit2, gated: profileOnly || !promoteEnabled, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },
      { group: 'Promote', label: 'Sales Pipeline', path: '/coach/crm', icon: Target, gated: profileOnly || !promoteEnabled, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },

      // Profit
      { group: 'Profit', label: 'Profit Center', path: '/coach/profit', icon: TrendingUp, gated: profileOnly || !profitEnabled, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },

      // Settings
      { group: 'Settings', label: 'AI Coach Settings', path: '/coach/ai-settings', icon: Settings, gated: !hasAIAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Requires Advanced tier' },
      { group: 'Settings', label: 'AI Coach Usage', path: '/coach/ai-usage', icon: BarChart2, gated: !hasAIAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Requires Advanced tier' },
      { group: 'Settings', label: 'Account Settings', path: '/coach/account', icon: Settings },
    ];
  }

  if (nlpPracticeEnabled) {
    items.push({ group: 'Training', label: 'NLP Practice', path: '/coach/nlp-practice', icon: GraduationCap });
  }

  return (
    <SidebarLayout
      items={items}
      title="Coach"
      subtitle="Manage respondents & insights"
      headerSlot={<RoleSwitcher currentRole="coach" />}
    />
  );
}
